/*
 * The navigation bar for every redesigned route.
 *
 * Mounted by utils/SiteChrome, not by the pages themselves, so the bar is
 * identical on the landing page, About, How it works and the whole sign-in
 * flow — and so adding a route to that set is a one-line change in one file.
 *
 * It therefore has to be self-contained: it carries its own dark wrapper and
 * gutters rather than assuming a parent supplies them, and it reads the
 * session itself instead of taking it as a prop. The session read is free —
 * createAuth is memoised, so this shares the very client the Zodiac launcher
 * below it uses.
 *
 * The theme switcher from the old header is deliberately not reproduced:
 * these pages are dark only, and a toggle that visibly does nothing is worse
 * than an absent one.
 */
import { Link } from "react-router-dom";
// @ts-ignore — synced from zodiac-embed, plain JS, no types
import ZodiacLauncher from "../ZodiacLauncher/ZodiacLauncher";
import { useZodiacSession } from "../Home/useZodiacSession";
import lockup from "../../assets/header-icons/indexx-logo-v3-dark.svg";
import s from "./landing.module.css";

export default function LandingHeader() {
  const { status, firstName } = useZodiacSession();
  const signedIn = status === "authenticated";
  const initial = (firstName || "").trim().charAt(0).toUpperCase();

  return (
    /* Its own .root: this renders above pages that know nothing about the
       redesign's colours, and the reset inside .root is what wins against the
       global `* { font-family: Poppins }` and the bare `h1..h6` colour rules. */
    <div className={s.root}>
      <div className={s.shell}>
        <header className={s.header}>
          <Link to="/" aria-label="indexx.ai home">
            <img src={lockup} alt="indexx.ai" className={s.lockup} />
          </Link>

          {/* <div>, not <nav>: Header_test/style.css styles the bare `nav`
              type selector as position:fixed with a dark background, and it
              is loaded on every route as an import side effect. */}
          <div className={s.nav}>
            <Link className={`${s.navLink} ${s.navHideOnPhone}`} to="/about">
              About
            </Link>
            <Link className={`${s.navLink} ${s.navHideOnPhone}`} to="/how-master-login-works">
              How it works
            </Link>

            {status === "unknown" ? (
              /* Reserve the space rather than guess. The silent session check
                 settles after first paint; rendering "Log in" and swapping it
                 for someone's name is a visible correction. */
              <span className={s.navLink} aria-hidden="true">&nbsp;</span>
            ) : signedIn ? (
              /* Names the destination on hover. The avatar is an initial in a
                 circle and the label is a first name — neither says that this
                 is the way to account settings. */
              <Link className={s.account} to="/indexx-exchange/account-settings" title="Account settings">
                <span className={s.avatar} aria-hidden="true">{initial || " "}</span>
                <span>{firstName || "Account"}</span>
              </Link>
            ) : (
              <>
                <Link className={s.navLink} to="/auth/login">Log in</Link>
                <Link className={`${s.navLink} ${s.navStrong}`} to="/auth/signup-email">
                  Register
                </Link>
              </>
            )}

            {/* The nine ovals the design draws ARE this component. Same props
                the global header passed, so createAuth's memo key matches and
                this is the same client instance — and mounting it here is what
                keeps the silent session check firing on these routes now that
                HeaderTest does not render on them. */}
            <ZodiacLauncher
              /* indexx.ai is the estate's front door, not one of the sixteen,
                 so there is no row for the panel to mark "you are here".
                 Passed explicitly because the type declares it required. */
              productId={undefined}
              tone="dark"
              auth={{
                clientId: "indexx-web",
                /*
                 * No reload when the session lands — this bar re-renders.
                 *
                 * useZodiacSession listens for "zodiac:session" and re-reads
                 * the client, so the header, Landing's greeting and the login
                 * page's bounce all correct themselves in place. The reload
                 * zodiacAuth falls back to is expensive: it throws away the
                 * in-memory tokens, so the tab re-authenticates from the
                 * refresh token afterwards — a second /id/token grant and a
                 * second /session/legacy mint for one sign-in — and it is the
                 * "Signing you in…" flash on the estate's front door.
                 *
                 * Only these routes. HeaderTest keeps the reload: the legacy
                 * pages under it read localStorage while mounting and have no
                 * listener, so removing their rescue would leave them empty.
                 * The flag lives on the memoised client, and SiteChrome mounts
                 * exactly one of the two headers per route, so the two settings
                 * never meet.
                 */
                reloadOnSession: false,
              }}
            />
          </div>
        </header>
      </div>
    </div>
  );
}
